import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Marketing pages have no session to restore — skip /me so the console stays clean.
    if ([
      "/",
      "/privacy",
      "/terms",
      "/login",
      "/forgot-password",
      "/reset-password",
      "/setup-password",
    ].includes(window.location.pathname)) {
      setUser(false);
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    // Step 1 returns an OTP challenge; no session exists until the code is verified.
    const { data } = await api.post("/auth/login/start", { email, password });
    return data;
  };

  const verifyOtp = async (challenge_id, otp) => {
    const { data } = await api.post("/auth/login/verify", { challenge_id, otp });
    setUser(data.user);
    return data.user;
  };

  const resendOtp = async (challenge_id) => {
    const { data } = await api.post("/auth/login/resend", { challenge_id });
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  const can = useCallback((permission) => Boolean(user?.permissions?.[permission]), [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, login, verifyOtp, resendOtp,
                                   logout, can, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
